//* Importing from libraries
import { useEffect, useState } from "react";

//* Importing from react-bootstrap
import { Button, Form, Spinner } from "react-bootstrap";

//* Importing from components
import { Attention } from "../../../component/base/Attention";
import { ErrorMessages } from "../../../component/common/ErrorMessages";
import { RestoreModal } from "../../../component/base/RestoreModal"; 

//* Importing from hooks from react-router-dom
import { useNavigate, useParams } from "react-router-dom";

//* Importing from Material UI icons
import { ArrowBack, SaveRounded } from "@material-ui/icons";

//* Importing from custom hooks
import { useCollaborator } from "../../../hooks/collaborators/useCollaborator";

export function UsersCollaboratorForm({ editMode }: { editMode?: boolean }) {
  const navigate = useNavigate();
  const params = useParams();
  const token = localStorage.getItem("token") || "";

  const [formData, setFormData] = useState({
    name: "",
    email: "",
  });
  const [resetPassword, setResetPassword] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [originalData, setOriginalData] = useState<any>(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [submitErrors, setSubmitErrors] = useState<string[]>([]);

  const [showRestoreModal, setShowRestoreModal] = useState<boolean>(false);

  const {
    getCollaboratorById,
    createCollaboratorAsync,
    updateCollaborator,
    restoreByEmailCollaborator,
    isCreating,
    isUpdating,
    isDeleting,
  } = useCollaborator(token);

  // Fetch collaborator data in edit mode
  useEffect(() => {
    const fetchCollaboratorData = async () => {
      if (editMode && params.id) {
        setIsLoadingData(true);
        try {
          const collaboratorData = await getCollaboratorById(params.id);
          if (collaboratorData) {
            const newFormData = {
              name: collaboratorData.person.name || "",
              email: collaboratorData.person.email || "",
            };
            setFormData(newFormData);
            setOriginalData(collaboratorData);
          }
        } catch (error) {
          console.error("Error fetching collaborator data:", error);
        } finally {
          setIsLoadingData(false);
        }
      }
    };

    fetchCollaboratorData();
  }, [editMode, params.id, getCollaboratorById]);

  // Real-time validation for the Name field
  useEffect(() => {
    if (formData.name.length > 0 && formData.name.length < 3) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        name: "Nome deve ter no mínimo 3 letras.",
      }));
    } else {
      setErrors((prevErrors) => {
        const { name, ...rest } = prevErrors;
        return rest;
      });
    }
  }, [formData.name]);

  // Real-time validation for the Email field
  useEffect(() => {
    const emailRegex = /\S+@\S+\.\S+/;
    if (formData.email.length > 0 && !emailRegex.test(formData.email)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: "Por favor, insira um email válido.",
      }));
    } else {
      setErrors((prevErrors) => {
        const { email, ...rest } = prevErrors;
        return rest;
      });
    }
  }, [formData.email]);

  // Function to validate the form before submission
  const validateForm = () => {
    const errorsList: { [key: string]: string } = {};

    if (formData.name.length < 3) {
      errorsList.name = "Nome deve ter no mínimo 3 letras.";
    }

    const emailRegex = /\S+@\S+\.\S+/;
    if (!emailRegex.test(formData.email)) {
      errorsList.email = "Por favor, insira um email válido.";
    }

    return errorsList;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errorsList = validateForm();
    if (Object.keys(errorsList).length > 0) {
      setErrors(errorsList);
      return;
    }

    try {
      if (editMode && params.id) {
        const updateData: any = {
          name: formData.name,
        };

        if (resetPassword) {
          updateData.reset_password = true;
        }

        await updateCollaborator({
          id: params.id,
          collaboratorData: updateData,
        });
        navigate("/users-collaborator");
      } else {
        await createCollaboratorAsync({
          name: formData.name,
          email: formData.email,
        });
        navigate("/users-collaborator");
      }
    } catch (err: any) {
      console.error("Operation failed:", err);

      // Verifica se o erro é um AxiosError
      if (err.status) {
        if (err.status === 409) {
          setSubmitErrors(["Email já está registrado."]);
        } else if (err.status === 410) {
          setShowRestoreModal(true);
        } else {
          setSubmitErrors([
            "Ocorreu um erro ao salvar os dados. Por favor, tente novamente.",
          ]);
        }
      } else {
        setSubmitErrors([
          "Ocorreu um erro ao salvar os dados. Por favor, tente novamente.",
        ]);
      }
    }
  };

  const handleRestoreEmail = async () => {
    if (formData.email) {
      try {
        // Supondo que a API permita restaurar pelo email
        await restoreByEmailCollaborator(formData.email);
        setShowRestoreModal(false);
        navigate("/users-collaborator");
      } catch (err) {
        console.error("Erro ao restaurar o email:", err);
        setSubmitErrors([
          "Ocorreu um erro ao restaurar o email. Por favor, tente novamente.",
        ]);
      }
    }
  };

  const isLoading = isCreating || isUpdating || isLoadingData || isDeleting;

  return (
    <div className="row">
      <div className="col-12">
        <div className="py-5 px-3">
          <div className="card-body" id="card">
            <h4 className="mt-0 header-title">
              {editMode ? "Editar" : "Cadastrar"} Colaborador
            </h4>
            <p>
              {editMode
                ? "Edite um colaborador do seu sistema"
                : "Cadastre um colaborador do seu sistema"}
            </p>
            <br />

            {submitErrors.length > 0 && (
              <ErrorMessages
                messages={submitErrors}
                clearErrors={() => setSubmitErrors([])}
              />
            )}

            {!isLoading && (
              <Form onSubmit={handleSubmit} noValidate>
                <div className="row">
                  <div className="col-lg">
                    <Form.Group className="mb-3">
                      <Form.Label htmlFor="nome">
                        Nome <Attention />
                      </Form.Label>
                      <Form.Control
                        id="nome"
                        placeholder="Nome"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        isInvalid={!!errors.name}
                        required
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.name}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>

                  <div className="col-lg">
                    <Form.Group className="mb-3">
                      <Form.Label htmlFor="email">
                        E-mail <Attention />
                      </Form.Label>
                      <Form.Control
                        id="email"
                        type="email"
                        placeholder="E-mail"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        disabled={editMode}
                        isInvalid={!!errors.email}
                        required
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.email}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>
                </div>

                {editMode && (
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="reset-password-switch"
                      label="Resetar senha"
                      checked={resetPassword}
                      onChange={(e) => setResetPassword(e.target.checked)}
                    />
                    <Form.Text className="text-muted">
                      Ative essa opção para enviar uma nova senha para o colaborador.
                    </Form.Text>
                  </Form.Group>
                )}

                <div className="w-100 d-flex justify-content-between mt-3">
                  <Button
                    variant="secondary"
                    className="d-flex align-items-center"
                    onClick={() => navigate("/users-collaborator")}
                    type="button"
                  >
                    <ArrowBack className="me-2" /> Voltar
                  </Button>
                  <Button
                    variant="success"
                    type="submit"
                    disabled={isLoading}
                    className="d-flex align-items-center"
                  >
                    <SaveRounded className="me-2" /> Salvar
                  </Button>
                </div>
              </Form>
            )}

            {isLoading && (
              <div className="d-flex justify-content-center align-items-center mt-3">
                <Spinner animation="border" variant="primary" role="status" />
              </div>
            )}

            {/* Modal de restauração para erro 410 */}
            <RestoreModal
              show={showRestoreModal}
              onHide={() => setShowRestoreModal(false)}
              entityName="Colaborador"
              onRestore={handleRestoreEmail}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
